import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown, ChevronUp, BookOpen, Languages } from "lucide-react";
import ChaptersPanel from "./ChaptersPanel";

const inputCls = "w-full px-4 py-2.5 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm";

function LevelCheckboxes({ levels, selected, onToggle }) {
  return (
    <div className="w-full" data-testid="assignment-levels-section">
      <label className="eyebrow block mb-2">Niveaux enseignés (groupes de langue)</label>
      <div className="flex flex-wrap gap-3">
        {levels.map((lv) => (
          <label key={lv} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={selected.includes(lv)}
              onChange={() => onToggle(lv)}
              data-testid={`assignment-level-checkbox-${lv}`}
            />
            {lv}
          </label>
        ))}
      </div>
      <div className="mt-1 text-xs text-[var(--muted)]">
        Cochez un ou plusieurs niveaux — ou laissez vide pour une affectation classique par classe.
      </div>
    </div>
  );
}

function AssignmentForm({ form, setForm, teachers, subjects, classes, langLevels, onAdd }) {
  const byLevels = form.levels.length > 0;
  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[160px]">
          <label className="eyebrow block mb-1">Professeur</label>
          <select className={inputCls} value={form.teacher_id} onChange={(e) => setForm((s) => ({ ...s, teacher_id: e.target.value }))} data-testid="assignment-teacher-select">
            <option value="">— Choisir —</option>
            {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="eyebrow block mb-1">Matière</label>
          <select className={inputCls} value={form.subject_id} onChange={(e) => setForm((s) => ({ ...s, subject_id: e.target.value, levels: [] }))} data-testid="assignment-subject-select">
            <option value="">— Choisir —</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="eyebrow block mb-1">Classe {byLevels && <span className="text-[var(--muted)]">(ignorée : niveaux cochés)</span>}</label>
          <select className={inputCls} value={form.class_id} onChange={(e) => setForm((s) => ({ ...s, class_id: e.target.value }))} disabled={byLevels} data-testid="assignment-class-select">
            <option value="">— Choisir —</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <button className="btn-primary !py-2.5 !px-4 text-sm flex items-center gap-1.5" onClick={onAdd} data-testid="add-assignment-btn">
          <Plus size={14} /> Affecter
        </button>
      </div>
      {langLevels && (
        <LevelCheckboxes
          levels={langLevels}
          selected={form.levels}
          onToggle={(lv) =>
            setForm((s) => ({ ...s, levels: s.levels.includes(lv) ? s.levels.filter((x) => x !== lv) : [...s.levels, lv] }))
          }
        />
      )}
    </div>
  );
}

function AssignmentRow({ a, expanded, onToggle, confirmDelete, onAskDelete, onConfirmDelete, onChanged }) {
  return (
    <div data-testid={`assignment-mgmt-row-${a.id}`}>
      <div className="py-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-medium flex items-center gap-2">
            {a.subject?.name} · <span className="text-[var(--maroon)]">{a.class?.name}</span>
            {a.level && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--maroon)]/10 text-[var(--maroon)] text-[10px] uppercase tracking-wider font-semibold" data-testid={`assignment-level-badge-${a.id}`}>
                <Languages size={11} /> Groupe {a.level}
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--ink-soft)]">
            {a.teacher?.name} · {a.chapter_count} chapitre{a.chapter_count > 1 ? "s" : ""} au programme
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
            onClick={onToggle}
            data-testid={`toggle-chapters-${a.id}`}
          >
            <BookOpen size={13} /> Programme {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {confirmDelete ? (
            <button className="!py-1.5 !px-3 text-xs bg-[var(--late)] text-white" onClick={onConfirmDelete} data-testid={`confirm-delete-assignment-${a.id}`}>
              Confirmer ?
            </button>
          ) : (
            <button className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5 !text-[var(--late)]" onClick={onAskDelete} data-testid={`delete-assignment-${a.id}`}>
              <Trash2 size={13} /> Supprimer
            </button>
          )}
        </div>
      </div>
      {expanded && <ChaptersPanel assignmentId={a.id} onChanged={onChanged} />}
    </div>
  );
}

const validateAssignmentForm = (form) => {
  if (!form.teacher_id || !form.subject_id) return "Sélectionnez un professeur et une matière";
  if (form.levels.length === 0 && !form.class_id) return "Sélectionnez une classe ou cochez des niveaux";
  return null;
};

const assignmentBody = (form) =>
  form.levels.length > 0
    ? { teacher_id: form.teacher_id, subject_id: form.subject_id, levels: form.levels }
    : { teacher_id: form.teacher_id, subject_id: form.subject_id, class_id: form.class_id };

function useAssignmentsData() {
  const [assignments, setAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [langDefs, setLangDefs] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [a, t, c, s, l] = await Promise.all([
      api.get("/admin/assignments"),
      api.get("/admin/teachers"),
      api.get("/admin/classes"),
      api.get("/subjects"),
      api.get("/language-levels"),
    ]);
    setAssignments(a.data);
    setTeachers(t.data);
    setClasses(c.data);
    setSubjects(s.data);
    setLangDefs(Object.fromEntries(l.data.map((d) => [d.subject.id, d.levels])));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { assignments, teachers, classes, subjects, langDefs, loading, load };
}

function useAssignmentActions(load) {
  const [form, setForm] = useState({ teacher_id: "", subject_id: "", class_id: "", levels: [] });
  const [confirmId, setConfirmId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const add = async () => {
    const error = validateAssignmentForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    try {
      await api.post("/admin/assignments", assignmentBody(form));
      setForm({ teacher_id: "", subject_id: "", class_id: "", levels: [] });
      toast.success(form.levels.length > 0 ? "Groupes de niveau affectés" : "Affectation créée");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/assignments/${id}`);
      setConfirmId(null);
      toast.success("Affectation supprimée");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return { form, setForm, confirmId, setConfirmId, expandedId, setExpandedId, add, remove };
}

export default function AssignmentsManager() {
  const { assignments, teachers, classes, subjects, langDefs, loading, load } = useAssignmentsData();
  const { form, setForm, confirmId, setConfirmId, expandedId, setExpandedId, add, remove } = useAssignmentActions(load);

  return (
    <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid="assignments-manager">
      <div className="pb-4 border-b border-[var(--parchment-2)]">
        <div className="eyebrow">Affectations professeur × matière × classe / niveau</div>
        <h2 className="font-serif-display text-2xl font-semibold mt-1">{assignments.length} affectation{assignments.length > 1 ? "s" : ""}</h2>
      </div>

      <AssignmentForm
        form={form}
        setForm={setForm}
        teachers={teachers}
        subjects={subjects}
        classes={classes}
        langLevels={langDefs[form.subject_id] || null}
        onAdd={add}
      />

      {loading ? (
        <div className="mt-6 text-[var(--ink-soft)]">Chargement...</div>
      ) : (
        <div className="mt-4 divide-y divide-[var(--parchment-2)]" data-testid="assignments-list">
          {assignments.map((a) => (
            <AssignmentRow
              key={a.id}
              a={a}
              expanded={expandedId === a.id}
              onToggle={() => setExpandedId(expandedId === a.id ? null : a.id)}
              confirmDelete={confirmId === a.id}
              onAskDelete={() => setConfirmId(a.id)}
              onConfirmDelete={() => remove(a.id)}
              onChanged={load}
            />
          ))}
          {assignments.length === 0 && <div className="py-4 text-sm text-[var(--ink-soft)]">Aucune affectation.</div>}
        </div>
      )}
    </div>
  );
}
